import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Callout from 'components/Callout';
import Message from 'components/Message';
import Spinner from 'components/Spinner';
import { BackNavLink, Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { makePath } from 'helpers/link-generation';
import { selectActiveVenue, selectRouteParams } from 'selectors/general';

import { toComparableId } from 'features/Sku/Items/SkuMenuData.normalizers';
import useSkuVenuePermissions from 'features/Sku/Items/useSkuVenuePermissions';
import { SKU_MODIFIERS_TAB, SKU_TAB_PARAM } from 'features/Sku/Items/useSkuMenuState';

import ModifierGroupEditor, { MODIFIER_EDITOR_MODE } from './ModifierGroupEditor';
import useModifierGroups from './useModifierGroups';
import useModifierItemCatalogue from './useModifierItemCatalogue';
import messages from './ModifierGroupEditor.messages';
import './ModifierEditorPage.scss';

const { block, element } = bem('modifier-editor-page');

const TITLES = {
  [MODIFIER_EDITOR_MODE.CREATE]: messages.createTitle,
  [MODIFIER_EDITOR_MODE.EDIT]: messages.editTitle,
  [MODIFIER_EDITOR_MODE.READ_ONLY]: messages.readOnlyTitle,
};

export const getEditorMode = ({ groupId, isIntegrated }) => {
  if (!groupId) return MODIFIER_EDITOR_MODE.CREATE;
  return isIntegrated ? MODIFIER_EDITOR_MODE.READ_ONLY : MODIFIER_EDITOR_MODE.EDIT;
};

const ModifierEditorPage = () => {
  const navigate = useNavigate();
  const { venueId } = useSelector(selectRouteParams);
  // The store's copy of the params is written by an effect, so it still describes the previous
  // page on the first render after a navigation. The URL is right straight away, and mounting the
  // editor before its group is known would leave the form permanently blank.
  const { groupId } = useParams();
  const seed = useLocation().state?.seed || null;
  const { isIntegrated } = useSkuVenuePermissions();
  const venueIsResolved = Boolean(useSelector(selectActiveVenue)?.venueId);

  const groupsQuery = useModifierGroups(venueId);
  const { items } = useModifierItemCatalogue(venueId);

  const libraryPath = `${makePath.items(venueId)}?${SKU_TAB_PARAM}=${SKU_MODIFIERS_TAB}`;
  const returnToLibrary = () => navigate(libraryPath);

  const mode = getEditorMode({ groupId, isIntegrated });
  const isLoadingGroup = Boolean(groupId) && groupsQuery.isLoading;
  const group = groupId
    ? (groupsQuery.data || []).find(candidate => toComparableId(candidate.core.id) === toComparableId(groupId))
    : null;
  const loadFailed = Boolean(groupId) && groupsQuery.isError;
  const isMissing = Boolean(groupId) && !isLoadingGroup && !loadFailed && !group;
  // The route's gate passes anyone who owns some manual venue, not this one, so authoring is
  // checked here against the venue in the URL, once that venue is actually known.
  const cannotAuthor = !groupId && venueIsResolved && isIntegrated;
  const sendToLibrary = isMissing || cannotAuthor;

  useEffect(() => {
    if (sendToLibrary) navigate(libraryPath, { replace: true });
  }, [sendToLibrary, libraryPath, navigate]);

  if (sendToLibrary) return null;

  return (
    <div className={block()}>
      <Title>
        <BackNavLink to={libraryPath} />
        <Message message={TITLES[mode]} />
      </Title>

      {loadFailed && (
        <Callout className={element('load-error')} variant="error">
          <Message as="text" message={messages.loadFailed} />
        </Callout>
      )}

      {isLoadingGroup ? (
        <div className={element('loading')}>
          <Spinner />
        </div>
      ) : (
        <ModifierGroupEditor
          venueId={venueId}
          mode={mode}
          group={group}
          seed={groupId ? null : seed}
          items={items}
          onCancel={returnToLibrary}
          onSaved={returnToLibrary}
        />
      )}
    </div>
  );
};

export default ModifierEditorPage;
